/**
 * Pure rules for "My Classes". The row-state / countdown / time-format rules
 * are a port of iflipweb-next's /rotations kiosk (features/rotations-display/
 * logic.ts, itself a port of iflipweb2's RotationViewStation), so a coach's
 * phone and the gym TV colour the same rotation the same way at the same
 * moment. The Now/Next selection is new to the app.
 *
 * Every function takes the clock it reasons about, so tests control it.
 */

import { ClassRotation, ClassStudent, MyClass } from '@/types/api';

// ── Chicago clock ────────────────────────────────────────────────────────────

/** "16:30" → 990. NaN for anything that isn't H:MM / HH:MM. */
export function parseHHMM(hhmm: string): number {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!m) return NaN;
  return Number(m[1]) * 60 + Number(m[2]);
}

let chicagoFormat: Intl.DateTimeFormat | null | undefined;

function getChicagoFormat(): Intl.DateTimeFormat | null {
  if (chicagoFormat !== undefined) return chicagoFormat;
  try {
    chicagoFormat = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    chicagoFormat = null;
  }
  return chicagoFormat;
}

/** Seconds since Chicago midnight (device-local time if Intl lacks time zones). */
export function chicagoSeconds(now: Date = new Date()): number {
  const fmt = getChicagoFormat();
  if (fmt) {
    try {
      const parts = fmt.formatToParts(now);
      const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
      const h = get('hour') % 24; // some engines render midnight as "24"
      const m = get('minute');
      const s = get('second');
      if (!Number.isNaN(h + m + s)) return h * 3600 + m * 60 + s;
    } catch {
      // fall through to device-local time
    }
  }
  return now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
}

// ── Row colour (legacy RotationViewStation.setStyle, via the kiosk) ─────────

/** current = green, ending = yellow (< 5 min left), ended = red (≤ 5 min ago), past = grey. */
export type RowState = 'current' | 'ending' | 'ended' | 'past' | 'future';

/**
 * Legacy order, later rules winning: within [start, end] → yellow when fewer
 * than 5 whole minutes remain, else green; then whole minutes till end < −5 →
 * grey, else ≤ 0 → red. Whole minutes truncate toward zero.
 */
export function rowState(r: ClassRotation, nowSec: number): RowState {
  const start = parseHHMM(r.start) * 60;
  const end = parseHHMM(r.end) * 60;
  const minutesTillEnd = Math.trunc((end - nowSec) / 60);
  let state: RowState = 'future';
  if (start <= nowSec && nowSec <= end) {
    state = minutesTillEnd >= 0 && minutesTillEnd < 5 ? 'ending' : 'current';
  }
  if (minutesTillEnd < -5) state = 'past';
  else if (minutesTillEnd <= 0) state = 'ended';
  return state;
}

/** Whether `r` is running at nowSec (start ≤ now ≤ end). */
export function isRunning(r: ClassRotation, nowSec: number): boolean {
  return parseHHMM(r.start) * 60 <= nowSec && nowSec <= parseHHMM(r.end) * 60;
}

/** Time left, legacy style: "1h5m", "12m"; under a minute "<1m". */
export function formatRemaining(seconds: number): string {
  const minutes = Math.floor(Math.max(0, seconds) / 60);
  if (minutes === 0) return '<1m';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h > 0 ? `${h}h` : ''}${m > 0 || h === 0 ? `${m}m` : ''}`;
}

/** "16:05" → "4:05" (12-hour, no AM/PM — the kiosk row format). */
export function formatRowTime(hhmm: string): string {
  const minutes = parseHHMM(hhmm);
  if (Number.isNaN(minutes)) return hhmm;
  const h24 = Math.floor(minutes / 60);
  const mm = minutes % 60;
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${mm < 10 ? '0' : ''}${mm}`;
}

/** "16:05" → "4:05 PM". */
export function formatClockTime(hhmm: string): string {
  const minutes = parseHHMM(hhmm);
  if (Number.isNaN(minutes)) return hhmm;
  return `${formatRowTime(hhmm)} ${minutes < 12 * 60 ? 'AM' : 'PM'}`;
}

/** A rotation id stable within the day (React key). */
export function rotationKey(r: ClassRotation): string {
  return `${r.start}-${r.end}-${r.apparatus}-${r.station}`;
}

export const GROUP_LABELS: Record<MyClass['group'], string> = {
  preschool: 'Preschool',
  'girls-rec': 'Girls Rec',
  'boys-rec': 'Boys Rec',
  tumble: 'Tumble',
  team: 'Team',
  other: 'Other',
};

/** Badge after an extra's name (same labels as the kiosk's logic.ts); null for a regular. */
export function studentBadge(s: ClassStudent): string | null {
  switch (s.kind) {
    case 'makeup':
      return 'MU';
    case 'trial':
      return 'TRIAL';
    case 'oneday':
      return '1-DAY';
    default:
      return null;
  }
}

// ── Now / Next ──────────────────────────────────────────────────────────────

/**
 * A class's span in seconds since midnight: the staff member's shift widened
 * to cover its rotations (rotations start a few minutes after the shift and
 * a long shift can outlast the last rotation).
 */
export function classWindow(cls: MyClass): { start: number; end: number } {
  let start = parseHHMM(cls.start);
  let end = parseHHMM(cls.end);
  for (const r of cls.rotations) {
    start = Math.min(start, parseHHMM(r.start));
    end = Math.max(end, parseHHMM(r.end));
  }
  return { start: start * 60, end: end * 60 };
}

/**
 * The class happening now and the one after it. "Now" is a class whose window
 * contains nowSec; with overlaps (team coaches hold several level shifts at
 * once) the one with a running rotation wins, then the latest to start. "Next"
 * is the earliest class starting after nowSec. Classes must be sorted by start.
 */
export function pickNowNext(
  classes: MyClass[],
  nowSec: number
): { now: MyClass | null; next: MyClass | null } {
  let now: MyClass | null = null;
  let nowScore = -Infinity;
  let next: MyClass | null = null;
  for (const cls of classes) {
    const w = classWindow(cls);
    if (w.start <= nowSec && nowSec <= w.end) {
      const running = cls.rotations.some((r) => isRunning(r, nowSec));
      const score = (running ? 1e6 : 0) + w.start;
      if (score > nowScore) {
        now = cls;
        nowScore = score;
      }
    } else if (w.start > nowSec && (next === null || w.start < classWindow(next).start)) {
      next = cls;
    }
  }
  return { now, next };
}

/**
 * The rotation to feature on a class card: the running one (the latest to
 * start if two touch at a boundary), else the next to start, else null.
 */
export function focusRotation(
  cls: MyClass,
  nowSec: number
): { rotation: ClassRotation; running: boolean } | null {
  let running: ClassRotation | null = null;
  let upcoming: ClassRotation | null = null;
  for (const r of cls.rotations) {
    if (isRunning(r, nowSec)) running = r;
    else if (upcoming === null && parseHHMM(r.start) * 60 > nowSec) upcoming = r;
  }
  if (running) return { rotation: running, running: true };
  return upcoming ? { rotation: upcoming, running: false } : null;
}

// ── Day picker ──────────────────────────────────────────────────────────────

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const pad = (n: number) => String(n).padStart(2, '0');

/** yyyy-MM-dd ± days (calendar arithmetic in UTC, so no DST surprises). */
export function addDays(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d + days));
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

/** "2026-10-23" → "Friday, Oct 23". */
export function formatDayLabel(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return `${WEEKDAYS[date.getUTCDay()]}, ${MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}`;
}
